import "./SocialMediaPost.css";
import cn from "classnames";
import { type FC, useMemo } from "react";
import { Link } from "react-router-dom";
import {
	isBlogPlatform,
	isSocialPlatform,
	Platform,
	type PostDto,
	PostStatus,
	postContraintCheck,
	postLabel,
	tiktokPostProgressLabel,
	xPostUrl,
	youtubePostProgressLabel,
	youtubeShortsUrl,
} from "shared";
import { Icon } from "../common/Icon";
import { PlatformIcon } from "../common/PlatformIcon";
import { SimpleTooltip } from "../common/SimpleTooltip";
import { LinkGen } from "../LinkGen";

interface SocialMediaPostProps {
	post: PostDto;
	className?: string;
	onApprove?: (postId: number) => void;
	canApprove?: boolean;
	requirePostApproval?: boolean;
}

export const SocialMediaPost: FC<Readonly<SocialMediaPostProps>> = ({
	post,
	className,
	onApprove,
	canApprove = false,
	requirePostApproval = false,
}) => {
	const { valid, errorMessage } = useMemo(
		() =>
			postContraintCheck(post.platform, post.content, post.files, undefined, {
				status: post.status,
				refId: post.refId,
			}),
		[post],
	);

	const showApproveButton =
		canApprove &&
		onApprove &&
		(post.status === PostStatus.PENDING_APPROVAL ||
			(requirePostApproval && post.status === PostStatus.DRAFT));

	const statusTooltip =
		post.status === PostStatus.FAILED
			? post.errorMessage || "Publish failed"
			: post.publishStatus === "held"
				? post.statusDetail || "Held in publish queue…"
				: post.publishStatus === "retrying"
					? post.statusDetail || "Retrying publish…"
					: post.status === PostStatus.IN_PROGRESS
						? post.platform === Platform.TIKTOK
							? tiktokPostProgressLabel(post.publishStatus, post.statusDetail)
							: post.platform === Platform.YOUTUBE
								? youtubePostProgressLabel(
										post.publishStatus,
										post.statusDetail,
									)
								: post.statusDetail || "Publishing…"
						: null;

	const livePostLink = useMemo(() => {
		if (!post.refId) {
			return undefined;
		}
		if (post.platform === Platform.YOUTUBE) {
			return { href: youtubeShortsUrl(post.refId), label: "View Short" };
		}
		if (post.platform === Platform.X) {
			return { href: xPostUrl(post.refId), label: "View on X" };
		}
		return undefined;
	}, [post.platform, post.refId]);

	return (
		<div className={cn("social-media-post-wrapper", className)}>
			<div className="social-media-post">
				<Link
					className="social-media-post-main"
					to={
						isSocialPlatform(post.platform)
							? LinkGen.Post(post.id)
							: isBlogPlatform(post.platform)
								? LinkGen.BlogPost(post.id)
								: LinkGen.Dashboard()
					}
				>
					{!valid && (
						<SimpleTooltip tooltip={errorMessage} placement="bottom">
							<span className="circle" />
						</SimpleTooltip>
					)}
					{post.status === PostStatus.PENDING_APPROVAL && (
						<SimpleTooltip tooltip="Pending approval" placement="bottom">
							<span className="circle circle-warning" />
						</SimpleTooltip>
					)}
					{statusTooltip && (
						<SimpleTooltip tooltip={statusTooltip} placement="bottom">
							<span
								className={cn("circle", {
									"circle-danger": post.status === PostStatus.FAILED,
									"circle-warning":
										post.status === PostStatus.IN_PROGRESS ||
										post.publishStatus === "retrying",
								})}
							/>
						</SimpleTooltip>
					)}
					<span className="subject">{postLabel(post)}</span>
					<PlatformIcon value={post.platform} />
				</Link>
				{livePostLink && (
					<a
						className="social-media-post-live-link"
						href={livePostLink.href}
						target="_blank"
						rel="noopener noreferrer"
					>
						{livePostLink.label}
					</a>
				)}
			</div>
			{showApproveButton && (
				<button
					type="button"
					className="social-media-post-approve-btn"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						onApprove(post.id);
					}}
				>
					<SimpleTooltip tooltip="Approve" placement="top">
						<Icon name="check_circle" size={14} />
					</SimpleTooltip>
				</button>
			)}
		</div>
	);
};
